import { Fields } from '@engoo/view-model';
import { CACHE_BUSTER_PARAM } from 'app/models/request/constants';
import { getIsBehindBrokenProxy } from 'app/remote/client/selectors';
import coreRequest from 'app/utils/request';
import { applyMixins } from 'app/utils/mixins';
import { ResponseError } from 'app/constants/ResponseErrors';
import { Error } from 'app/constants/schemas';
import ApplicationError from 'app/models/request/ApplicationError';
import FluentdLogger from 'app/services/fluentdLogger';

export const withCacheBuster = (request) => (options = {}) => (dispatch, getState) => {
  // Clone the options so we aren't mutating it.
  const wrappedOptions = { ...options };

  // If the user is behind a proxy, add a cache buster to the request (if it is a GET).
  if (options.method === 'GET' && getIsBehindBrokenProxy(getState())) {
    const cacheBuster = `${Date.now()}-${Math.random()}`;
    wrappedOptions.data = {
      ...wrappedOptions.data,
      [CACHE_BUSTER_PARAM]: cacheBuster,
    };
  }

  return request(wrappedOptions)(dispatch, getState);
};

const probablyApplicationError = (response) =>
  response &&
  typeof response === 'object' &&
  typeof response.error === 'object' &&
  response.error[Fields.TYPE] === Error.typeName;

/**
 * Converts general errors to ApplicationErrors when appropriate.
 *
 * Most errors returned from the backend will be in this format, so we
 * try to convert all of them, and fall back to the `request` error if
 * coercion fails.
 */
export const withApplicationErrors = (request) => (options = {}) => (dispatch, getState) =>
  request(options)(dispatch, getState).catch((error) => {
    if (error instanceof ResponseError &&
        probablyApplicationError(error.response)) {
      try {
        return Promise.reject(ApplicationError.parseResponseError(error));
      } catch (e) {
        // the error was unable to be parsed.
        FluentdLogger.info({
          name:    'ApplicationError.parseResponseError',
          message: e.message,
        });

        return Promise.reject(error);
      }
    } else {
      // the error doesn't look like an application error
      return Promise.reject(error);
    }
  });

/**
 * Our composable middleware results in the type
 *   (options) => (dispatch, getState) => Promise
 * But the request we're wrapping has interface
 *   (options) => Promise
 * So transform it to the first signature to make composition easier.
 */
export const thunkRequest = (options = {}) => (dispatch, getState) => coreRequest(options);

/**
 * Modifies the options parameter to include special headers/fields based on the state.
 *
 * @param {Object} options
 */
export const request = applyMixins(
  withApplicationErrors,
  withCacheBuster,
)(thunkRequest);
